import { Box } from '@mui/material';
import React, { useRef } from 'react';
import ImageCarouselSlideImagePreview from './ImageCarouselSlideImagePreview';
import { handleImageZonePaste } from './handleImageZonePaste';
import { VIDEO_ACCEPT, isVideoFile } from './useCardVideoUpload';

interface ImageCarouselSlideImageZoneProps {
  index: number;
  imageUrl?: string;
  altText?: string;
  uploading: boolean;
  onFile: (file: File) => void;
  /** When set, a dropped/picked video file is routed here instead of ignored. */
  onVideoFile?: (file: File) => void;
  /** When set, a pasted YouTube or Vimeo link is routed here (turning the slide into a video slide). */
  onVideoUrl?: (url: string) => void;
}

/**
 * One carousel slide's image: click, drag, or paste-while-focused all
 * set/replace it via `onFile`. Video files go to `onVideoFile` and pasted
 * external video links to `onVideoUrl`, when provided.
 * Distinct from the slide's hover controls (paste button, delete) and
 * content fields (title, description).
 */
export default function ImageCarouselSlideImageZone({
  index,
  imageUrl,
  altText,
  uploading,
  onFile,
  onVideoFile,
  onVideoUrl,
}: ImageCarouselSlideImageZoneProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const accept = onVideoFile ? `image/*,${VIDEO_ACCEPT}` : 'image/*';

  const takeFile = (file: File | undefined) => {
    if (!file) return;
    if (isVideoFile(file) && onVideoFile) onVideoFile(file);
    else if (file.type.startsWith('image/')) onFile(file);
  };

  return (
    <>
      <Box
        tabIndex={0}
        role="button"
        aria-label={`Slide ${index + 1} image — click, drag, or paste to set`}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          takeFile(event.dataTransfer.files[0]);
        }}
        onClick={() => fileInputRef.current?.click()}
        onPaste={(event) => handleImageZonePaste(event, onFile, onVideoUrl)}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          bgcolor: '#f5f5f5',
          height: 160,
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          outline: 'none',
          '&:hover .img-overlay': { opacity: 1 },
          '&:focus-visible': {
            boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}`,
          },
        }}
      >
        <ImageCarouselSlideImagePreview
          uploading={uploading}
          imageUrl={imageUrl}
          altText={altText}
        />
      </Box>

      <input
        type="file"
        accept={accept}
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(event) => {
          takeFile(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </>
  );
}
